import { Skeleton } from "@kobalte/core/skeleton";
import { Navigate, Router, useLocation } from "@solidjs/router";
import {
  createEffect,
  type JSX,
  lazy,
  type ParentComponent,
  Show,
  Suspense,
} from "solid-js";
import { ProtectedRoute } from "@/domains/auth/routes/protected-route";
import { PublicRoute } from "@/domains/auth/routes/public-route";
import { EligibilityGuard } from "@/domains/billing/components/EligibilityGuard";
import { PlanLimitWatcher } from "@/domains/billing/components/PlanLimitWatcher";
import { SearchLayout } from "@/domains/company/active-search/components/SearchLayout";
import { ReportGenerationRouteSync } from "@/domains/company/report/components/report-generation-route-sync";
import { GlobalErrorManager } from "@/domains/error/GlobaErrorManager";
import { CookieBanner } from "@/domains/tracking/components/cookie-banner";
import { CookiePreferencesPanel } from "@/domains/tracking/components/cookie-preferences-panel";
import { GlobalFooter } from "@/domains/tracking/components/global-footer";
import AppProviderManager from "./AppProviderManager";
import { CompanyLayoutSkeleton } from "./components/layout/CompanyLayout";
import { SkeletonDetailLayout } from "./components/layout/DetailLayout";
import { ProfileLayoutSkeleton } from "./components/layout/ProfileLayout";
import { Loading } from "./components/ui/loading";
import { FreelancerApplicationsRoute } from "./domains/application/router/FreelancerApplicationsRoute";
import LogoutRoute from "./domains/auth/router/logout";
import Onboarding from "./domains/auth/router/onboarding/OnboardingRoute";
import SignUp from "./domains/auth/router/signup/SignUpRoute";
import Verify from "./domains/auth/router/verify/VerifyRoute";
import { isBillingEnabled } from "./domains/billing/utils/is-billing-enabled";
import Pricing from "./domains/business/router/PricingRoute";
import CompanySearchFreelancerNewProposalRoute from "./domains/company/router/profile/company/search/freelancer/new-proposal/route";
import CompanySearchFreelancerCreateJobOfferRoute from "./domains/company/router/profile/company/search/freelancer/offers/create/route";
import CompanySearchFreelancerOffersRoute from "./domains/company/router/profile/company/search/freelancer/offers/route";
import { CompanySearchFreelancerResultGuardedRoute } from "./domains/company/router/profile/company/search/freelancer/result/guarded-route";
import CompanySearchFreelancerSearchRoute from "./domains/company/router/profile/company/search/freelancer/search/route";
import CompanySearchFreelancerSendProposalRoute from "./domains/company/router/profile/company/search/freelancer/send-proposal/route";
import NotFoundRoute from "./domains/error/router/NotFoundRoute";
import { FreelancerProposalsRoute } from "./domains/freelance/router/profile/proposals/route";
import { RefinePage } from "./domains/job-offer/ai/refine/components/RefinePage";
import { RefinePreviewPage } from "./domains/job-offer/ai/refine/components/RefinePreviewPage";
import Home from "./domains/job-offer/router/HomeRoute";
import { JobOfferRoute } from "./domains/job-offer/router/jobs-offers/[id]/route";
import JobOfferErrorRoute from "./domains/job-offer/router/jobs-offers/create/error/route";
import JobOfferCreateRedirectRoute from "./domains/job-offer/router/jobs-offers/create/redirect/route";
import JobOfferSuccessRoute from "./domains/job-offer/router/jobs-offers/create/success/route";
import { FakeJobOffer } from "./domains/job-offer/router/jobs-offers/fake/route";
import { usePostHog } from "./hooks/usePostHog";
import { importOrReload } from "./libs/import-reloader";

const ProposalDetailRoute = lazy(() =>
  importOrReload(
    () => import("./domains/freelance/router/profile/proposals/[id]/route"),
  ),
);

const Auth = lazy(() =>
  importOrReload(() => import("./domains/auth/router/login/LoginRoute")),
);

const PublicProfile = lazy(() =>
  importOrReload(
    () => import("./domains/freelance/router/profile/[segment]/route"),
  ),
);

const CompanySettingsRoute = lazy(() =>
  importOrReload(
    () => import("./domains/company/router/profile/company/route"),
  ),
);

const CompanyDashboardRoute = lazy(() =>
  importOrReload(
    () => import("./domains/company/router/profile/company/dashboard-route"),
  ),
);

const CompanyAtsRoute = lazy(() =>
  importOrReload(
    () => import("./domains/company/router/profile/company/ats/route"),
  ),
);

const CompanyTalentPoolRoute = lazy(() =>
  importOrReload(
    () => import("./domains/company/router/profile/company/talent-pool/route"),
  ),
);

const CompanySearchFreelancerRoute = lazy(() =>
  importOrReload(
    () =>
      import(
        "./domains/company/router/profile/company/search/freelancer/route"
      ),
  ),
);

const JobOfferDetailRoute = lazy(() =>
  importOrReload(
    () =>
      import("./domains/company/router/profile/company/ats/[jobOfferId]/route"),
  ),
);

const CompanyMembersRoute = lazy(() =>
  importOrReload(
    () => import("./domains/company/router/profile/company/members/route"),
  ),
);

const CompanyCreateRoute = lazy(() =>
  importOrReload(() => import("./domains/company/router/company/create/route")),
);

const CompanyFindCandidatesRoute = lazy(() =>
  importOrReload(
    () =>
      import("./domains/company/router/profile/company/find-candidates/route"),
  ),
);

const AiJobOfferRoute = lazy(() =>
  importOrReload(
    () => import("./domains/job-offer/router/jobs-offers/create/ai/route"),
  ),
);

const RefineRoute = lazy(() =>
  importOrReload(
    () => import("./domains/job-offer/router/jobs-offers/create/refine/route"),
  ),
);

const AcceptInvitationRoute = lazy(() =>
  importOrReload(
    () => import("./domains/company/router/accept-invitation/route"),
  ),
);

const InviteFreelancerRoute = lazy(() =>
  importOrReload(
    () =>
      import("./domains/freelance/router/invite/freelancer/[username]/route"),
  ),
);

const AcceptCompanyInvitationRoute = lazy(() =>
  importOrReload(
    () => import("./domains/company/router/accept-company-invitation/route"),
  ),
);

const MemberOnboardingRoute = lazy(() =>
  importOrReload(
    () =>
      import("./domains/auth/router/onboarding/member/MemberOnboardingRoute"),
  ),
);

const PublicCompanyRoute = lazy(() =>
  importOrReload(() => import("./domains/company/router/company/[id]/route")),
);

const JobOfferApplicationRoute = lazy(() =>
  importOrReload(
    () => import("./domains/application/router/JobOfferApplicationRoute"),
  ),
);

const PoliciesRoute = lazy(() =>
  importOrReload(() => import("./domains/business/router/PoliciesRoute")),
);

const ProfileTabRoute = lazy(() =>
  importOrReload(() => import("./domains/freelance/router/profile/me/route")),
);

// The guarded module, not the route: the role check is a unit with its own test
// (`guarded-route.test.tsx`), because nothing imports this file in the suite.
const FreelanceDashboardRoute = lazy(() =>
  importOrReload(
    () => import("./domains/freelance/router/dashboard/guarded-route"),
  ),
);

const PartnersRoute = lazy(() =>
  importOrReload(
    () => import("./domains/business/router/profile/partners/route"),
  ),
);

const FreelancerToolkitRoute = lazy(() =>
  importOrReload(
    () => import("./domains/freelance/router/profile/toolkit/route"),
  ),
);

const PublicToolkitRoute = lazy(() =>
  importOrReload(() => import("./domains/freelance/router/toolkit/route")),
);

const CalcolatoreTassePIVARoute = lazy(() =>
  importOrReload(
    () =>
      import("./domains/freelance/router/toolkit/calcolatore-tasse-piva/route"),
  ),
);

const CalcolatoreTariffaGiornalieraRoute = lazy(() =>
  importOrReload(
    () =>
      import(
        "./domains/freelance/router/toolkit/calcolatore-tariffa-giornaliera/route"
      ),
  ),
);

const CalcolatoreTariffaOrariaRedirect = () => (
  <Navigate href="/freelancer/toolkit/calcolatore-tariffa-giornaliera" />
);

const CalcolatoreRisparmioFreelanceDipendenteRoute = lazy(() =>
  importOrReload(
    () =>
      import(
        "./domains/freelance/router/toolkit/calcolatore-risparmio-freelance-dipendente/route"
      ),
  ),
);

const CalcolatoreRisparmioFreelanceDipendenteProtectedRoute = lazy(() =>
  importOrReload(
    () =>
      import(
        "./domains/freelance/router/profile/toolkit/calcolatore-risparmio-freelance-dipendente/route"
      ),
  ),
);

const CompanyOnboardingRoute = lazy(() =>
  importOrReload(
    () =>
      import("./domains/auth/router/onboarding/company/CompanyOnboardingRoute"),
  ),
);

const IntentRoute = lazy(() =>
  importOrReload(() => import("./domains/auth/router/intent/IntentRoute")),
);

const BillingSettingsRoute = lazy(() =>
  importOrReload(
    () =>
      import("./domains/billing/router/billing-settings/BillingSettingsRoute"),
  ),
);

const UpgradeRoute = lazy(() =>
  importOrReload(() => import("./domains/billing/router/upgrade/UpgradeRoute")),
);

const UpgradeSuccessRoute = lazy(() =>
  importOrReload(
    () => import("./domains/billing/router/upgrade/UpgradeSuccessRoute"),
  ),
);

const UpgradeReviewRoute = lazy(() =>
  importOrReload(
    () => import("./domains/billing/router/upgrade/UpgradeReviewRoute"),
  ),
);

const TrackerProvider: ParentComponent = (props) => {
  const location = useLocation();
  const { capture, isInitialized } = usePostHog();

  createEffect(() => {
    if (isInitialized() && location.pathname) {
      capture("$pageview", {
        $current_url: import.meta.env.VITE_PUBLIC_APP_URL + location.pathname,
      });
    }
  });

  return (
    <>
      {/*
        Colonna a piena altezza attorno a outlet + footer.

        Senza di essa il footer sta in flusso normale subito sotto l'outlet, e
        finché la route lazy non ha risolto l'outlet è alto quanto il fallback
        della sua `Suspense` — 120px per `/company/:id`. Il footer finiva così
        appoggiato all'header per tutto il caricamento (misurato: ~2,9s su
        `/company/seed_company_1`), per poi scendere di colpo. Il difetto era
        di ogni rotta, perché `TrackerProvider` è la radice di tutte.

        `flex-1` sull'outlet gli fa occupare lo spazio che avanza, così il
        footer resta a fondo viewport mentre il contenuto è corto. Non elimina
        il salto — quando arriva una pagina lunga il footer scende comunque —
        ma lo sposta fuori dallo schermo, che è tutto quello che serve.

        Non cambia nulla a regime: i layout montati (`ProfileLayout`,
        `CompanyLayout`, `AuthLayout`, …) portano già `min-h-screen`, quindi
        superano da soli l'altezza minima di questa colonna.

        Gli overlay restano fuori: sono `fixed`/portal e dentro una colonna
        flex diventerebbero item di layout senza motivo.
      */}
      <div class="flex min-h-dvh flex-col">
        <div class="flex-1">{props.children}</div>
        {/* Global in-app footer + persistent "Preferenze cookie" re-entry point,
            in normal flow after the outlet so it is reachable on every layout
            (T5 audit). AuthLayout drops its own inline footer to avoid duplication. */}
        <GlobalFooter />
      </div>
      {/* Non-modal cookie consent banner: mounted above the outlet so it persists
          across SPA navigation until the user chooses (SPEC AC 1, 12). */}
      <CookieBanner />
      {/* Single preferences dialog instance, opened from the banner or the footer
          via the shared `preferencesOpen` state. */}
      <CookiePreferencesPanel />
      {/* L'unica risposta a una quota di piano esaurita, per ogni flusso e per
          tutti e tre i limiti. Sta qui, dentro il router, perché la sua CTA
          naviga verso i piani: montato accanto agli altri provider — che sono
          fuori da `<Router>` — l'unica uscita sarebbe un reload pieno. */}
      <PlanLimitWatcher />
    </>
  );
};

const BillingGuard: ParentComponent = (props) => {
  return (
    <Show when={isBillingEnabled()} fallback={<Navigate href="/404" />}>
      {props.children}
    </Show>
  );
};

const RootRouteShell: ParentComponent = (props) => (
  <TrackerProvider>
    {/* Inside the router on purpose: the report-generation provider lives above
        it and cannot call `useLocation()` itself. */}
    <ReportGenerationRouteSync />
    {props.children}
  </TrackerProvider>
);

const router = {
  path: "/",
  component: RootRouteShell,
  children: [
    /* Superficie di prototipo: viva solo in dev, mai nel bundle di produzione.
       Il `lazy(...)` deve restare dentro il ramo DEV: in build
       `import.meta.env.DEV` diventa `false` e Rollup scarta il ramo morto con
       il suo `import()`. Hoistato a livello di modulo, il `lazy(...)` resta
       (Rollup non lo sa puro) e con lui il chunk dell'harness. */
    ...(import.meta.env.DEV
      ? [
          {
            path: "/prototypes/report-loader-bar",
            component: lazy(() =>
              importOrReload(
                () => import("./prototypes/report-loader-bar/harness"),
              ),
            ),
          },
        ]
      : []),
    {
      path: "/",
      component: Home,
    },
    {
      path: "/pricing",
      component: () => (
        <BillingGuard>
          <Pricing />
        </BillingGuard>
      ),
    },
    {
      path: "/legal/policies",
      component: PoliciesRoute,
    },
    {
      path: "/auth",
      component: PublicRoute,
      children: [
        {
          path: "/",
          component: Auth,
        },
      ],
    },
    {
      path: "/auth/signup",
      component: PublicRoute,
      children: [
        {
          path: "/",
          component: SignUp,
        },
      ],
    },

    {
      path: "/auth/intent",
      component: IntentRoute,
    },
    {
      path: "/auth/onboarding",
      component: Onboarding,
    },
    {
      path: "/auth/onboarding/company",
      component: CompanyOnboardingRoute,
    },
    {
      path: "/auth/onboarding/member",
      component: () => (
        <Suspense fallback={<Loading />}>
          <MemberOnboardingRoute />
        </Suspense>
      ),
    },
    {
      path: "/verify-account",
      component: Verify,
    },
    {
      path: "/auth/logout",
      component: LogoutRoute,
    },
    {
      path: "/profile",
      component: ProtectedRoute,
      children: [
        {
          path: "/",
          component: () => (
            <Suspense fallback={<ProfileLayoutSkeleton />}>
              <ProfileTabRoute />
            </Suspense>
          ),
        },
        {
          path: "/applications",
          component: () => (
            <Suspense fallback={<ProfileLayoutSkeleton />}>
              <FreelancerApplicationsRoute />
            </Suspense>
          ),
        },
        {
          path: "/proposals/:id",
          component: () => (
            <Suspense fallback={<SkeletonDetailLayout />}>
              <ProposalDetailRoute />
            </Suspense>
          ),
        },
        {
          path: "/proposals",
          component: () => (
            <Suspense fallback={<ProfileLayoutSkeleton />}>
              <FreelancerProposalsRoute />
            </Suspense>
          ),
        },
        {
          path: "/partners",
          component: () => (
            <Suspense fallback={<ProfileLayoutSkeleton />}>
              <PartnersRoute />
            </Suspense>
          ),
        },
        {
          path: "/toolkit",
          component: () => (
            <Suspense fallback={<ProfileLayoutSkeleton />}>
              <FreelancerToolkitRoute />
            </Suspense>
          ),
        },
        {
          path: "/toolkit/calcolatore-risparmio-freelance-dipendente",
          component: () => (
            <Suspense fallback={<ProfileLayoutSkeleton />}>
              <CalcolatoreRisparmioFreelanceDipendenteProtectedRoute />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: "/freelancer/toolkit/calcolatore-risparmio-freelance-dipendente",
      component: () => (
        <Suspense fallback={<ProfileLayoutSkeleton />}>
          <CalcolatoreRisparmioFreelanceDipendenteRoute />
        </Suspense>
      ),
    },
    {
      path: "/freelancer/toolkit/calcolatore-tariffa-giornaliera",
      component: () => (
        <Suspense fallback={<ProfileLayoutSkeleton />}>
          <CalcolatoreTariffaGiornalieraRoute />
        </Suspense>
      ),
    },
    {
      path: "/freelancer/toolkit/calcolatore-tariffa-oraria",
      component: CalcolatoreTariffaOrariaRedirect,
    },
    {
      path: "/freelancer/toolkit/calcolatore-tasse-piva",
      component: () => (
        <Suspense fallback={<ProfileLayoutSkeleton />}>
          <CalcolatoreTassePIVARoute />
        </Suspense>
      ),
    },
    {
      path: "/freelancer/toolkit",
      component: () => (
        <Suspense fallback={<ProfileLayoutSkeleton />}>
          <PublicToolkitRoute />
        </Suspense>
      ),
    },
    {
      // `:segment` e non `:id`: la stessa rotta risolve id, username e
      // username precedente (D1) — dalla forma non si distinguono.
      path: "/profile/:segment",
      component: () => (
        <Suspense fallback={<SkeletonDetailLayout />}>
          <PublicProfile />
        </Suspense>
      ),
    },
    {
      // Top-level and not under `/profile`: the dashboard is a page of its own, and
      // `/profile/company` is the precedent for a top-level `ProtectedRoute` family.
      path: "/freelance/dashboard",
      component: ProtectedRoute,
      children: [
        {
          path: "/",
          component: () => (
            <Suspense fallback={<ProfileLayoutSkeleton />}>
              <FreelanceDashboardRoute />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: "/profile/company",
      component: ProtectedRoute,
      children: [
        {
          path: "/",
          component: () => (
            <Suspense fallback={<CompanyLayoutSkeleton />}>
              <CompanyDashboardRoute />
            </Suspense>
          ),
        },
        {
          path: "/settings",
          component: () => (
            <Suspense fallback={<CompanyLayoutSkeleton />}>
              <CompanySettingsRoute />
            </Suspense>
          ),
        },
        {
          path: "/ats",
          component: () => (
            <Suspense fallback={<CompanyLayoutSkeleton />}>
              <CompanyAtsRoute />
            </Suspense>
          ),
        },
        {
          path: "/ats/:jobOfferId",
          component: () => (
            <Suspense fallback={<CompanyLayoutSkeleton />}>
              <JobOfferDetailRoute />
            </Suspense>
          ),
        },
        {
          path: "/find-candidates",
          component: () => (
            <Suspense fallback={<CompanyLayoutSkeleton />}>
              <CompanyFindCandidatesRoute />
            </Suspense>
          ),
        },
        {
          path: "/talent-pool",
          component: () => (
            <Suspense fallback={<CompanyLayoutSkeleton />}>
              <CompanyTalentPoolRoute />
            </Suspense>
          ),
        },
        {
          path: "/search/freelancers",
          component: SearchLayout,
          children: [
            {
              path: "/",
              component: () => (
                <Suspense fallback={<CompanyLayoutSkeleton />}>
                  <CompanySearchFreelancerRoute />
                </Suspense>
              ),
            },
            {
              path: "/offers",
              component: () => (
                <Suspense fallback={<CompanyLayoutSkeleton />}>
                  <CompanySearchFreelancerOffersRoute />
                </Suspense>
              ),
            },
            {
              path: "/search",
              // A from-scratch search creates a new offer, so it consumes an
              // offer slot. Block it on quota exhaustion — the choice-page button
              // is already disabled, this closes the deep-link/bookmark bypass.
              // The from-offer flow (/offers → /result) is unaffected: it reuses
              // an existing offer and never routes through /search.
              component: () => (
                <EligibilityGuard action="offer-creation">
                  <Suspense fallback={<CompanyLayoutSkeleton />}>
                    <CompanySearchFreelancerSearchRoute />
                  </Suspense>
                </EligibilityGuard>
              ),
            },
            {
              path: "/result",
              // The `offer-creation` quota guard and its two exemptions (AC 10,
              // AC 10b) live in the domain, next to the route they guard, and are
              // covered by `guarded-route.test.tsx` — inline here they were
              // untestable, and four mutations of the rule passed the whole suite.
              component: CompanySearchFreelancerResultGuardedRoute,
            },

            {
              path: "/new-proposal",
              component: () => (
                <Suspense fallback={<CompanyLayoutSkeleton />}>
                  <CompanySearchFreelancerNewProposalRoute />
                </Suspense>
              ),
            },
            {
              path: "/send-proposal",
              component: () => (
                <Suspense fallback={<CompanyLayoutSkeleton />}>
                  <CompanySearchFreelancerSendProposalRoute />
                </Suspense>
              ),
            },
            {
              path: "/create-job-offer",
              component: () => (
                <Suspense fallback={<CompanyLayoutSkeleton />}>
                  <CompanySearchFreelancerCreateJobOfferRoute />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "/members",
          component: () => (
            <Suspense fallback={<CompanyLayoutSkeleton />}>
              <CompanyMembersRoute />
            </Suspense>
          ),
        },
        {
          path: "/job-offers/create",
          component: () => <Navigate href="/job-offers/create/ai" />,
        },
        {
          path: "/job-offers/new",
          component: () => <Navigate href="/job-offers/create/ai" />,
        },
        {
          path: "/settings/billing",
          component: () => (
            <BillingGuard>
              <Suspense fallback={<CompanyLayoutSkeleton />}>
                <BillingSettingsRoute />
              </Suspense>
            </BillingGuard>
          ),
        },
        {
          path: "/upgrade",
          component: () => (
            <BillingGuard>
              <Suspense fallback={<CompanyLayoutSkeleton />}>
                <UpgradeRoute />
              </Suspense>
            </BillingGuard>
          ),
        },
        {
          path: "/upgrade/review",
          component: () => (
            <BillingGuard>
              <Suspense fallback={<CompanyLayoutSkeleton />}>
                <UpgradeReviewRoute />
              </Suspense>
            </BillingGuard>
          ),
        },
        {
          path: "/upgrade/success",
          component: () => (
            <BillingGuard>
              <Suspense fallback={<CompanyLayoutSkeleton />}>
                <UpgradeSuccessRoute />
              </Suspense>
            </BillingGuard>
          ),
        },
      ],
    },
    {
      path: "/company/create",
      component: () => (
        <Suspense fallback={<Loading />}>
          <CompanyCreateRoute />
        </Suspense>
      ),
    },
    {
      path: "/accept-invitation",
      component: () => (
        <Suspense fallback={<Loading />}>
          <AcceptInvitationRoute />
        </Suspense>
      ),
    },
    {
      path: "/invite/freelancer/:username",
      component: () => (
        <Suspense fallback={<Loading />}>
          <InviteFreelancerRoute />
        </Suspense>
      ),
    },
    {
      path: "/accept-company-invitation",
      component: () => (
        <Suspense fallback={<Loading />}>
          <AcceptCompanyInvitationRoute />
        </Suspense>
      ),
    },
    {
      path: "/job-offers/create",
      component: ProtectedRoute,
      children: [
        {
          path: "/",
          // Old links and bookmarks land on the AI chat, the only creation path.
          component: () => <Navigate href="/job-offers/create/ai" />,
        },
        {
          path: "/start",
          component: () => (
            <Suspense fallback={<Loading />}>
              <JobOfferCreateRedirectRoute />
            </Suspense>
          ),
        },
        {
          path: "/ai",
          component: () => (
            <EligibilityGuard action="offer-creation">
              <Suspense fallback={<Loading />}>
                <AiJobOfferRoute />
              </Suspense>
            </EligibilityGuard>
          ),
        },
        {
          path: "/refine",
          component: (props: { children?: JSX.Element }) => (
            <EligibilityGuard action="offer-creation">
              <Suspense fallback={<Loading />}>
                <RefineRoute>{props.children}</RefineRoute>
              </Suspense>
            </EligibilityGuard>
          ),
          children: [
            {
              path: "/",
              component: () => (
                <Suspense fallback={<Loading />}>
                  <RefinePage />
                </Suspense>
              ),
            },
            {
              path: "/preview",
              component: () => (
                <Suspense fallback={<Loading />}>
                  <RefinePreviewPage />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "/success",
          component: JobOfferSuccessRoute,
        },
        {
          path: "/error",
          component: JobOfferErrorRoute,
        },
      ],
    },
    {
      path: "/job-offers/:id",
      children: [
        {
          path: "/",
          component: () => (
            <Suspense fallback={<SkeletonDetailLayout />}>
              <JobOfferRoute />
            </Suspense>
          ),
        },
        {
          path: "/apply",
          component: () => (
            <Suspense fallback={<SkeletonDetailLayout />}>
              <ProtectedRoute>
                <JobOfferApplicationRoute />
              </ProtectedRoute>
            </Suspense>
          ),
        },
      ],
    },
    {
      path: "/test/job-application",
      component: FakeJobOffer,
    },
    {
      path: "/company/:id",
      component: () => (
        <Suspense
          fallback={
            <Skeleton
              class="w-full bg-gray-200 animate-pulse rounded-md"
              height={120}
            />
          }
        >
          <PublicCompanyRoute />
        </Suspense>
      ),
    },
    {
      path: "*",
      component: NotFoundRoute,
    },
  ],
};
//prod
const AppRouter = () => {
  return (
    <AppProviderManager>
      <GlobalErrorManager>
        <Router>{router}</Router>
      </GlobalErrorManager>
    </AppProviderManager>
  );
};

export default AppRouter;
